import {KeyboardEvent, RefObject, useEffect, useRef, useState} from 'react';

// Spread onto a chip's remove button; carries the ref, so it goes on the element itself
export type RemoveButtonProps = {
    ref: (element: HTMLButtonElement | null) => void;
    tabIndex: number;
    onFocus: () => void;
    onKeyDown: (event: KeyboardEvent) => void;
    onClick: () => void;
};

/**
 * Keyboard and focus for a group of chips, each with a remove button
 *
 * The group is one tab stop: only one remove button is in the tab order - the last focused, or
 * the first - the arrow keys move between them, wrapping at the ends, Home and End go to the first
 * and last, and Delete or Backspace removes the focused chip. A removal, by key or click, hands
 * focus to the chip that took its place, else the one before, else to `fallbackRef`, as the button
 * that had it is gone.
 *
 * @param keys - The removable chips, in display order
 * @param onRemove - Removes a chip; returns whether it did
 * @param fallbackRef - Where focus goes once no removable chip is left; it needs tabIndex={-1}
 * @returns The props for a chip's remove button
 */
export function useRemovableChips(
    keys: string[],
    onRemove: (key: string) => boolean,
    fallbackRef: RefObject<HTMLElement>,
): (key: string) => RemoveButtonProps {
    const [activeKey, setActiveKey] = useState<string | null>(null);
    const buttons = useRef(new Map<string, HTMLButtonElement>());
    // Set by a removal, and focused once the removed chip has gone; a null key means fallbackRef
    const focusAfterRemoval = useRef<{key: string | null} | null>(null);

    useEffect(() => {
        if (!focusAfterRemoval.current) {
            return;
        }
        const {key} = focusAfterRemoval.current;
        focusAfterRemoval.current = null;
        (key === null ? fallbackRef.current : buttons.current.get(key))?.focus();
    });

    const tabStop = activeKey !== null && keys.includes(activeKey) ? activeKey : keys[0];

    const focusAt = (index: number) => {
        buttons.current.get(keys[(index + keys.length) % keys.length])?.focus();
    };

    const remove = (key: string) => {
        const index = keys.indexOf(key);
        const next = keys[index + 1] ?? keys[index - 1] ?? null;
        if (onRemove(key)) {
            focusAfterRemoval.current = {key: next};
            setActiveKey(next);
        }
    };

    const handleKeyDown = (event: KeyboardEvent, key: string) => {
        const index = keys.indexOf(key);
        switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
            focusAt(index + 1);
            break;
        case 'ArrowLeft':
        case 'ArrowUp':
            focusAt(index - 1);
            break;
        case 'Home':
            focusAt(0);
            break;
        case 'End':
            focusAt(keys.length - 1);
            break;
        case 'Delete':
        case 'Backspace':
            remove(key);
            break;
        default:
            return;
        }
        // Ours, so the page neither scrolls nor navigates back
        event.preventDefault();
    };

    return (key) => ({
        ref: (element) => {
            if (element) {
                buttons.current.set(key, element);
            } else {
                buttons.current.delete(key);
            }
        },
        tabIndex: key === tabStop ? 0 : -1,
        onFocus: () => setActiveKey(key),
        onKeyDown: (event) => handleKeyDown(event, key),
        onClick: () => remove(key),
    });
}
